import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NodeProps, NodeResizer } from 'reactflow';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { NodeContextMenu } from '@/components/workflow/NodeContextMenu';
import { useWorkflowStore } from '@/stores/workflow-store';

interface LabelNodeData {
  content: string;
  color: 'yellow' | 'blue' | 'green' | 'pink' | 'purple' | 'orange';
  textSize?: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  textAlign?: 'left' | 'center' | 'right';
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
}

const colorClasses = {
  yellow: 'bg-yellow-100 border-yellow-300 text-yellow-900',
  blue: 'bg-blue-100 border-blue-300 text-blue-900',
  green: 'bg-green-100 border-green-300 text-green-900',
  pink: 'bg-pink-100 border-pink-300 text-pink-900',
  purple: 'bg-purple-100 border-purple-300 text-purple-900',
  orange: 'bg-orange-100 border-orange-300 text-orange-900',
};

const colorOptions = [
  { value: 'yellow', label: 'Yellow', class: 'bg-yellow-200' },
  { value: 'blue', label: 'Blue', class: 'bg-blue-200' },
  { value: 'green', label: 'Green', class: 'bg-green-200' },
  { value: 'pink', label: 'Pink', class: 'bg-pink-200' },
  { value: 'purple', label: 'Purple', class: 'bg-purple-200' },
  { value: 'orange', label: 'Orange', class: 'bg-orange-200' },
];

const textSizeOptions = [
  { value: 'sm', label: 'Small' },
  { value: 'base', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' },
  { value: '2xl', label: 'Huge' },
];

const textAlignOptions = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const textSizeClasses = {
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
};

const textAlignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const fontWeightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export const LabelNode: React.FC<NodeProps<LabelNodeData>> = ({
  data,
  selected,
  id,
}) => {
  const [content, setContent] = useState(data.content || '');
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { updateNode, deleteNode } = useWorkflowStore();

  useEffect(() => {
    setContent(data.content || '');
  }, [data.content]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (content !== data.content) {
      updateNode(id, { ...data, content });
    }
  }, [id, content, data, updateNode]);

  const handleColorChange = useCallback((color: string) => {
    updateNode(id, { ...data, color });
  }, [id, data, updateNode]);

  const handleTextSizeChange = useCallback((textSize: string) => {
    updateNode(id, { ...data, textSize });
  }, [id, data, updateNode]);

  const handleTextAlignChange = useCallback((textAlign: string) => {
    updateNode(id, { ...data, textAlign });
  }, [id, data, updateNode]);

  const handleToggleBold = useCallback(() => {
    const currentWeight = data.fontWeight || 'medium';
    const newWeight = currentWeight === 'bold' ? 'medium' : 'bold';
    updateNode(id, { ...data, fontWeight: newWeight });
  }, [id, data, updateNode]);

  const handleToggleItalic = useCallback(() => {
    const currentStyle = data.fontStyle || 'normal';
    const newStyle = currentStyle === 'italic' ? 'normal' : 'italic';
    updateNode(id, { ...data, fontStyle: newStyle });
  }, [id, data, updateNode]);

  const handleDelete = useCallback(() => {
    deleteNode(id);
  }, [id, deleteNode]);

  const textSize = data.textSize || 'lg';
  const textAlign = data.textAlign || 'left';
  const fontWeight = data.fontWeight || 'medium';
  const fontStyle = data.fontStyle || 'normal';

  return (
    <NodeContextMenu
      nodeId={id}
      nodeType="label"
      colorOptions={colorOptions}
      currentColor={data.color || 'yellow'}
      onColorChange={handleColorChange}
      textSizeOptions={textSizeOptions}
      currentTextSize={textSize}
      onTextSizeChange={handleTextSizeChange}
      textAlignOptions={textAlignOptions}
      currentTextAlign={textAlign}
      onTextAlignChange={handleTextAlignChange}
      isBold={fontWeight === 'bold'}
      onToggleBold={handleToggleBold}
      isItalic={fontStyle === 'italic'}
      onToggleItalic={handleToggleItalic}
      onDelete={handleDelete}
    >
      <div className="relative w-full h-full">
        {/* Resize handles - only show when selected */}
        <NodeResizer
          isVisible={selected}
          minWidth={150}
          minHeight={100}
          handleClassName="!w-3 !h-3 !bg-primary !border-2 !border-white !rounded-full"
          lineClassName="!border-primary"
        />

        <div
          className={cn(
            'label-node rounded-lg border-2 shadow-md transition-all',
            'hover:shadow-lg',
            colorClasses[data.color || 'yellow'],
            selected && 'ring-2 ring-primary ring-offset-2'
          )}
          style={{
            width: '100%',
            height: '100%',
            minWidth: '150px',
            minHeight: '100px',
          }}
        >
          {/* Drag handle at the top - invisible but functional */}
          <div
            className="drag-handle absolute top-0 left-0 right-0 h-6 cursor-move select-none z-10"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          />

          {/* Content area */}
          <div className="p-4 pt-6 h-full overflow-hidden">
            {isEditing ? (
              <Textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onBlur={handleBlur}
                placeholder="Type your label here..."
                className={cn(
                  'w-full h-full resize-none border-none focus-visible:ring-0',
                  'bg-transparent placeholder:text-current/40 leading-relaxed',
                  textSizeClasses[textSize],
                  textAlignClasses[textAlign],
                  fontWeightClasses[fontWeight],
                  fontStyle === 'italic' && 'italic'
                )}
                autoFocus
              />
            ) : (
              <div
                className={cn(
                  'w-full h-full leading-relaxed whitespace-pre-wrap break-words overflow-auto cursor-pointer',
                  textSizeClasses[textSize],
                  textAlignClasses[textAlign],
                  fontWeightClasses[fontWeight],
                  fontStyle === 'italic' && 'italic'
                )}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                {content || (
                  <span className="text-current/40">Double-click to add label...</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </NodeContextMenu>
  );
};

export default LabelNode;
